import { Injectable, signal } from '@angular/core';

export interface UploadTask {
  videoId: string;
  videoTitle: string;
  courseId: string;
  sectionId: string;
  progress: number;
  upchunkInstance: any; // `@mux/upchunk` instance
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  // Map to hold active upload tasks: Map<videoId, UploadTask>
  private activeUploadsMap = new Map<string, UploadTask>();

  // Expose signals for components to listen to progress reliably
  readonly uploads = signal<Map<string, UploadTask>>(new Map());

  // Subscribe to progress specific to a videoId. If not tracked, returns undefined.
  getProgress(videoId: string): number | undefined {
    return this.uploads().get(videoId)?.progress;
  }

  hasActiveUploads(): boolean {
    return this.activeUploadsMap.size > 0;
  }

  hasUpload(videoId: string): boolean {
    return this.activeUploadsMap.has(videoId);
  }

  // Set the Upchunk instance and initial status globally
  startTracking(videoId: string, videoTitle: string, courseId: string, sectionId: string, upchunkInstance: any): void {
    const task: UploadTask = {
      videoId,
      videoTitle,
      courseId,
      sectionId,
      progress: 0,
      upchunkInstance
    };

    this.activeUploadsMap.set(videoId, task);
    this.updateSignal();
  }

  // Update progress percentage
  updateProgress(videoId: string, progress: number): void {
    const task = this.activeUploadsMap.get(videoId);
    if (task) {
      task.progress = progress;
      this.updateSignal();
    }
  }

  /**
   * Called to completely remove an upload from tracking, such as on success or error.
   */
  removeUpload(videoId: string): void {
    this.activeUploadsMap.delete(videoId);
    this.updateSignal();
  }

  /**
   * Aborts the physical network Request via Mux Upchunk and removes from tracking.
   */
  abortAndRemove(videoId: string): void {
    const task = this.activeUploadsMap.get(videoId);
    if (task && task.upchunkInstance) {
      try {
        task.upchunkInstance.abort();
      } catch (e) {
        console.error('Failed to abort upchunk instance', e);
      }
    }
    this.removeUpload(videoId);
  }

  private updateSignal(): void {
    // Pass a fresh Map so signals trigger change detection automatically
    this.uploads.set(new Map(this.activeUploadsMap));
  }
}
